import {
  Box,
  Button,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState, useEffect } from 'react';

import {
  typeguards as commonTypeguards,
  date as commonDate,
  finleyAssert,
} from '@finley-technologies/common';
import {
  typeguards as finleyTypeguards,
  DataLake,
  CreditAgreement,
} from '@finley-technologies/finley-types';

import { useExecuteAthenaRecurringQueryByRawLazyQueryGQLType } from '../requests/requests.gql.generated';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type AthenaRecurringQueryExecuteModalProps = {
  organizationId: string;
  wipConfig: WipConfig;
  fullPropertyKey: string;
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  isOpen: boolean;
  onCancel: () => void;
  onQuerySuccess: (items: DataLake.AthenaQueryItemData[]) => void;
};
export const AthenaRecurringQueryExecuteModal = ({
  organizationId,
  wipConfig,
  fullPropertyKey,
  globalRecurringDateConfigs,
  presetVariables,
  inputDateType,
  isOpen,
  onCancel,
  onQuerySuccess,
}: AthenaRecurringQueryExecuteModalProps) => {
  const toast = useToast();

  const [inputDate, setInputDate] = useState<string>(
    commonDate.currentDateStr(),
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  const wipRecurringQuery = getIn(wipConfig.formProps.values, fullPropertyKey);
  finleyAssert(
    finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
      wipRecurringQuery,
    ),
    `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
  );

  const [
    executeAthenaRecurringQueryByRaw,
    { loading: isQueryLoading },
  ] = useExecuteAthenaRecurringQueryByRawLazyQueryGQLType({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ athenaRecurringQueryResponse }) => {
      onCancel();

      const { items, errors } = athenaRecurringQueryResponse;
      if (errors.length > 0) {
        toast({
          title: 'Athena Recurring Query Execute Error',
          description: errors.join(', '),
          status: 'error',
          position: 'top',
          duration: 8000,
          isClosable: true,
        });
        return;
      }

      if (items.length === 0) {
        toast({
          title: 'Athena Recurring Query Execute Update',
          description: 'Query returned 0 data points',
          status: 'info',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const recurringQueryCopy = _.cloneDeep(wipRecurringQuery);
      recurringQueryCopy.columnNames = Object.keys(items[0]);
      wipConfig.formProps.setFieldValue(fullPropertyKey, recurringQueryCopy);

      onQuerySuccess(items);
    },
    onError: () => {
      onCancel();
      toast({
        title: 'Athena Recurring Query Error',
        description:
          'Incurred server error when executing Athena recurring query',
        status: 'error',
        position: 'top',
        isClosable: true,
      });
    },
  });

  const onFullExecute = () => {
    if (inputDate.length === 0) {
      setErrorMessage('Can not have empty date input');
      return;
    }

    if (!commonTypeguards.isFullDateStr(inputDate)) {
      setErrorMessage(`"${inputDate}" is not valid Full Date String structure`);
      return;
    }

    const {
      globalRecurringDateConfigFields,
      variables,
      bindings,
      queryString,
      name,
    } = wipRecurringQuery;

    // If there's any overlap with provided "presetVariables", prioritize the
    // value that's set up with the modal through "supplementalVariables" and
    // filter out what already existed in "presetVariables"
    const updatedPresetVariables = presetVariables.filter((preset) => {
      return preset.name !== inputDateType;
    });

    executeAthenaRecurringQueryByRaw({
      variables: {
        organizationId,
        globalRecurringDateConfigsString: JSON.stringify(
          globalRecurringDateConfigs,
        ),
        globalRecurringDateConfigFields,
        supplementalVariables: [
          { name: inputDateType, value: inputDate, parseNumber: true },
        ],
        variables: [...updatedPresetVariables, ...variables],
        bindings,
        queryString,
        queryName: name,
      },
    });
  };

  useEffect(() => {
    setErrorMessage('');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent p="5">
        <ModalHeader>Execute Athena Recurring Query</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Flex alignItems="center" mb="7">
            <FormLabel w="48" m="0">
              Query Name:
            </FormLabel>
            <Text px="4">{wipRecurringQuery.name}</Text>
          </Flex>

          <Box mb="7">
            <Flex alignItems="center" mb="1">
              <FormLabel w="48" m="0">
                {_.startCase(inputDateType)}:
              </FormLabel>
              <Input
                w="56"
                type="text"
                value={inputDate}
                onChange={(evt) => {
                  setInputDate(evt.target.value);
                }}
              />
            </Flex>

            <Text fontStyle="italic" fontSize="sm">
              Note: this value is only important when referencing it as a
              variable or using a relative recurring date config
            </Text>
          </Box>

          {errorMessage && (
            <Alert status="error" mb="7" rounded="md">
              <AlertIcon />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <Flex justifyContent="center">
            <Button
              type="button"
              mr="5"
              isDisabled={!inputDate}
              isLoading={isQueryLoading}
              onClick={() => {
                onFullExecute();
              }}
            >
              Execute
            </Button>
            <Button onClick={() => onCancel()}>Close</Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
